import {
  CAPTURE_CAPABILITY,
  NAVIGATE_CAPABILITY,
  REFRESH_PLUGINS_CAPABILITY,
  isApiName,
  type ClientCapability,
  type SessionContext,
} from 'ts-components/PiChat/protocol';

export interface BusinessAppContext {
  targetType: 'business_app';
  appApiName?: string;
}

export type KiCodeTarget = {
  profile: 'business-app';
  context: BusinessAppContext;
};

export type KiCodeOpenArgs = KiCodeTarget & { initialPrompt?: string };

export type KiCodeProfile = KiCodeTarget['profile'];

export const businessAppOpenArgs = (appApiName?: string): KiCodeOpenArgs => ({
  profile: 'business-app',
  context:
    appApiName === undefined
      ? { targetType: 'business_app' }
      : { targetType: 'business_app', appApiName },
});

export const KICODE_PROFILES: Record<KiCodeProfile, ClientCapability[]> = {
  'business-app': [
    NAVIGATE_CAPABILITY,
    REFRESH_PLUGINS_CAPABILITY,
    CAPTURE_CAPABILITY,
  ],
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const BUSINESS_APP_CONTEXT_KEYS = new Set(['targetType', 'appApiName']);

/**
 * Total guard: returns false (never throws) for anything that isn't a
 * well-formed target for its profile, so it can vet untrusted input such as a
 * stored resume entry.
 */
export const isValidKiCodeTarget = (
  target: unknown
): target is KiCodeTarget => {
  if (!isRecord(target) || !isRecord(target.context)) return false;
  const { context } = target;
  switch (target.profile) {
    case 'business-app':
      return (
        context.targetType === 'business_app' &&
        (context.appApiName === undefined || isApiName(context.appApiName)) &&
        Object.keys(context).every((k) => BUSINESS_APP_CONTEXT_KEYS.has(k))
      );
    default:
      return false;
  }
};

export const isSameKiCodeTarget = (a: KiCodeTarget, b: KiCodeTarget) => {
  const left: SessionContext = a.context;
  const right: SessionContext = b.context;
  const keys = Object.keys({ ...left, ...right }) as (keyof SessionContext)[];
  return a.profile === b.profile && keys.every((k) => left[k] === right[k]);
};

export const hasKiCodeCapability = (
  target: KiCodeTarget,
  capability: ClientCapability
) => KICODE_PROFILES[target.profile].includes(capability);
